import { ListingCard } from "./components/listing-card";
import { BookOpen, Sunset, SunsetIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useMemo, useState } from "react";
import {
  closeTallyPopup,
  openTallyPopup,
  RequestDualStakeButton,
} from "./components/request-dualstake-tally";
import { useLocation, useNavigate } from "react-router-dom";
import { shortenNumber } from "@/lib/utils";
import { WithErrorAndLoading } from "@/components/with-error-and-loading";
import { useListings } from "@/hooks/use-listings";
import { useFarmsAndAPRs } from "@/hooks/use-farms-and-aprs";
import { SEO } from "@/components/seo";
import { useSortedListings } from "@/hooks/use-sorted-listings";
import { NotFoundIcon } from "@/components/notfound";
import { ListingSortDropdown } from "@/components/listing-sort-dropdown";
import DualStakeHero from "./components/ds-hero";
import { useAlgoAcctInfo } from "@/hooks/use-algo-account";
import { useWallet } from "@txnlab/use-wallet";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Link } from "@/components/links";

export function DualStakeIndex() {
  const { pathname } = useLocation();
  const {
    isLoading: loadingL,
    error: errorL,
    data: listings,
    refetch: refetchL,
  } = useListings();
  const {
    isLoading: loadingF,
    error: errorF,
    data: farmsAndAprs,
    refetch: refetchF,
  } = useFarmsAndAPRs(listings ? [...listings.values()] : []);

  const { activeAddress } = useWallet();
  const { data: sunsetListings } = useListings(undefined, true);
  const { data: acctInfo } = useAlgoAcctInfo(activeAddress ?? "");

  const hasSunset = useMemo(() => {
    if (!sunsetListings) return false;
    if (!acctInfo) return false;
    const lstIds = new Set([...sunsetListings?.values()].map((l) => l.lstId));
    return (acctInfo.assets ?? []).find(({ assetId: a, amount: am }) =>
      am > 0 && lstIds.has(BigInt(a))
    );
  }, [acctInfo, activeAddress, sunsetListings]);

  const loading = loadingL || loadingF;
  const error = errorL || errorF;
  const retry = () => {
    if (errorL) refetchL();
    if (errorF) refetchF();
  };

  const navigate = useNavigate();
  const showTally = pathname.endsWith("request");

  useEffect(() => {
    if (showTally) {
      openTallyPopup(() => navigate(`/dualSTAKE`));
    } else {
      closeTallyPopup();
    }
  }, [showTally]);

  const [showWithFarms, setShowWithFarms] = useState(true);
  const [showWithoutFarms, setShowWithoutFarms] = useState(true);

  const sortedListings = useSortedListings(showWithFarms, showWithoutFarms);

  const tvl = useMemo(
    () =>
      !listings
        ? "-"
        : [...listings.values()].reduce((tvl, { staked }) => tvl + staked, 0n),
    [listings]
  );
  return (
    <>
      <SEO
        type="product"
        title="Myth Finance dualSTAKE"
        description="Algorand Liquid Staking. Rewards in your favorite assets."
      />
      <WithErrorAndLoading
        loading={loading}
        error={error?.message}
        retry={retry}
      >
        <>
          <div className="flex flex-col items-center mb-4">
            <DualStakeHero />
            <div className="mt-4 -mb-3 text-sm sm:hidden w-full flex justify-between text-muted-foreground">
              <div>
                <span className="text-primary">{listings?.size}</span> dualSTAKE
                tokens
              </div>
              <div>
                <span className="text-primary">
                  {shortenNumber(Number(tvl) / 1e6)}
                </span>{" "}
                ALGO TVL
              </div>
            </div>
            <div className="w-full mt-8 flex fkex-wrap justify-between items-center text-muted-foreground gap-6">
              <Button
                variant="outline"
                size="sm"
                onClick={
                  () =>
                    (window.location.href = `https://docs.myth.finance/dualSTAKE/overview`) /* TODO dialog with overview */
                }
              >
                <BookOpen /> How does it work?
              </Button>
              <div className="hidden text-sm sm:block">
                <span className="text-primary">
                  {shortenNumber(Number(tvl) / 1e6)}
                </span>{" "}
                ALGO TVL
              </div>
              <ListingSortDropdown
                showWithFarms={showWithFarms}
                showWithoutFarms={showWithoutFarms}
                setShowWithFarms={setShowWithFarms}
                setShowWithoutFarms={setShowWithoutFarms}
              />
            </div>
          </div>

          {hasSunset ? (
            <Alert variant="destructive" className="max-w-[640px] w-fit">
              <SunsetIcon strokeWidth={2} className="h-6 w-6 -ml-1" />
              <AlertTitle className="font-bold">
                Redeem sunset tokens
              </AlertTitle>
              <AlertDescription>
                Some of your dualSTAKE tokens have <Link to="/dualSTAKE/sunset">been sunset</Link>.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="container flex flex-wrap justify-center items-center gap-4">
            {sortedListings.map((li, i) => (
              <ListingCard
                farmAndApr={farmsAndAprs?.get(li.appId)!}
                listing={li}
                key={`li-${i}`}
              />
            ))}
            {listings?.size && !sortedListings.length ? (
              <div className="flex  mt-12 text-lg flex-col items-center">
                <NotFoundIcon className="mb-8 text-muted-foreground" />
                <div className="text-muted-foreground">Nothing to show.</div>
                <div className="flex items-center gap-1">
                  Check your filter preferences.{" "}
                </div>
              </div>
            ) : null}
          </div>
          <div className="flex-grow"></div>
          <div className="flex flex-col md:flex-row gap-2">
            <Button
              variant="secondary"
              onClick={() => navigate("/dualSTAKE/sunset")}
            >
              <Sunset />
              Show Sunset Tokens
            </Button>
            <RequestDualStakeButton />
          </div>
        </>
      </WithErrorAndLoading>
    </>
  );
}
